import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, BookOpen, Sparkles, RefreshCw } from "lucide-react";
import { Container, Reveal } from "@/components/ui-kit";
import { LIBRARY_ICONS, LIBRARY_CATEGORIES } from "@/lib/hs-library-sample";
import { useLibraryDigest } from "@/lib/library-digest";
import { cn } from "@/lib/utils";

const catLabel = (key) => LIBRARY_CATEGORIES.find((c) => c.key === key)?.label.replace(/s$/, "") || key;

const DigestCard = ({ item, idx }) => {
  const Icon = LIBRARY_ICONS[item.cat] || BookOpen;
  const isNew = item.kind === "new";
  return (
    <Link to="/library" data-testid={`library-digest-item-${idx}`} className="card-dark group snap-start shrink-0 w-[240px] sm:w-[260px] p-4 flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-mk-muted"><Icon className="h-3.5 w-3.5 text-mk-lime" />{catLabel(item.cat)}</span>
        <span className={cn("inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider", isNew ? "bg-mk-lime/15 text-mk-lime" : "bg-amber-400/15 text-amber-300")} data-testid={`library-digest-item-${idx}-kind`}>
          {isNew ? <Sparkles className="h-2.5 w-2.5" /> : <RefreshCw className="h-2.5 w-2.5" />}{isNew ? "New" : "Updated"}
        </span>
      </div>
      <h3 className="mt-2 text-sm font-semibold text-mk-text leading-snug line-clamp-2">{item.title}</h3>
      {(item.note || item.trade) && <p className="mt-1.5 text-[11px] text-mk-muted line-clamp-2">{item.note || item.trade}</p>}
      <span className="mt-auto pt-3 inline-flex items-center gap-1 text-xs text-mk-dim group-hover:text-mk-lime transition-colors">See it in the library <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" /></span>
    </Link>
  );
};

const LibraryDigest = () => {
  const { digest, loaded } = useLibraryDigest();
  const row = useRef(null);
  const nudge = (dir) => row.current?.scrollBy({ left: dir * 280, behavior: "smooth" });
  if (!loaded || digest.visible === false || !digest.items.length) return null;
  const fresh = digest.items.filter((i) => i.kind === "new").length;
  const updated = digest.items.length - fresh;
  return (
    <section id="library-digest" className="border-y border-mk-border bg-mk-surface/40 overflow-x-clip" data-testid="library-digest">
      <Container className="py-12 lg:py-16">
        <div className="grid lg:grid-cols-[300px_minmax(0,1fr)] gap-8 lg:gap-12 items-start">
          <Reveal>
            <div className="eyebrow inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" />New this week in the library</div>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-mk-text text-balance">H&S templates, refreshed weekly.</h2>
            <p className="mt-3 text-sm text-mk-dim leading-relaxed" data-testid="library-digest-intro">{digest.intro}</p>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 font-mono text-[11px] text-mk-muted">
              <span data-testid="library-digest-week">{digest.week_label}</span>
              <span data-testid="library-digest-version">Library {digest.version}</span>
              <span data-testid="library-digest-counts"><span className="text-mk-lime">{fresh} new</span> · <span className="text-amber-300">{updated} updated</span></span>
            </div>
            <div className="mt-6 flex items-center gap-2">
              <Link to="/library" className="btn-ghost" data-testid="library-digest-browse">Browse the library <ArrowRight className="h-4 w-4" /></Link>
              {digest.items.length > 3 && (
                <div className="hidden lg:flex gap-1 ml-2">
                  <button type="button" onClick={() => nudge(-1)} aria-label="Previous titles" className="grid h-9 w-9 place-items-center rounded-full border border-mk-border text-mk-dim hover:text-mk-text hover:border-mk-strong transition-colors" data-testid="library-digest-prev"><ArrowLeft className="h-4 w-4" /></button>
                  <button type="button" onClick={() => nudge(1)} aria-label="Next titles" className="grid h-9 w-9 place-items-center rounded-full border border-mk-border text-mk-dim hover:text-mk-text hover:border-mk-strong transition-colors" data-testid="library-digest-next"><ArrowRight className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="min-w-0">
            <div ref={row} className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-px-5 sm:scroll-px-0 -mx-5 px-5 sm:mx-0 sm:px-0 pb-2 lg:[mask-image:linear-gradient(to_right,black_86%,transparent)]" data-testid="library-digest-items">
              {digest.items.map((it, i) => <DigestCard key={`${it.title}-${i}`} item={it} idx={i} />)}
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

export default LibraryDigest;
